
import React, { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import SEO from '../components/SEO';

interface Brochure {
  id: string;
  title: string;
  description: string | null;
  pdf_url: string;
  cover_image_url: string | null;
  created_at: string;
}

const fallbackBrochures = [
  {
    title: 'AI Compliance Framework',
    image: 'https://images.unsplash.com/photo-1589994965851-a8f479c573a9?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'Ethical AI Development',
    image: 'https://images.unsplash.com/photo-1677442135703-1787eea5ce01?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'AI Governance Policies',
    image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'Data Protection & AI',
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'AI Risk Assessment Guide',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80',
  },
  {
    title: 'AI in the Legal Industry',
    image: 'https://images.unsplash.com/photo-1505664194779-8beaceb93744?auto=format&fit=crop&w=600&q=80',
  },
];

const Brochures = () => {
  const [brochures, setBrochures] = useState<Brochure[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrochures = async () => {
      const { data, error } = await supabase
        .from('brochures')
        .select('*')
        .order('created_at', { ascending: false });

      if (!error && data) {
        setBrochures(data);
      }
      setLoading(false);
    };
    fetchBrochures();
  }, []);

  const hasDatabaseBrochures = brochures.length > 0;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title={`Brochures & Publications — Law and Code Africa`}
        description={`Browse Law and Code Africa's brochures and publications on AI compliance, ethical AI development, and AI governance frameworks.`}
        path="/brochures"
      />
      {/* Hero Section */}
      <section className="relative py-16 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=1920&q=80"
          alt="Legal brochures and documents on AI law"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 drop-shadow-lg">Brochures</h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto drop-shadow-lg">
              Explore our collection of informational brochures on AI law, ethics, and governance.
            </p>
          </div>
        </div>
      </section>

      {/* Brochures Grid */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-gray-200 rounded-lg mb-3" />
                  <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
                </div>
              ))}
            </div>
          ) : hasDatabaseBrochures ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {brochures.map((brochure) => (
                <a
                  key={brochure.id}
                  href={brochure.pdf_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group cursor-pointer block"
                >
                  <div className="aspect-[3/4] overflow-hidden rounded-lg shadow-md mb-3 bg-gray-100 flex items-center justify-center">
                    {brochure.cover_image_url ? (
                      <img
                        src={brochure.cover_image_url}
                        alt={brochure.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center p-4 text-center">
                        <svg className="w-12 h-12 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
                        </svg>
                        <span className="text-xs text-muted-foreground">PDF</span>
                      </div>
                    )}
                  </div>
                  <h3 className="text-sm md:text-base font-medium text-foreground text-center">{brochure.title}</h3>
                  {brochure.description && (
                    <p className="text-xs text-muted-foreground text-center mt-1">{brochure.description}</p>
                  )}
                </a>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {fallbackBrochures.map((brochure, index) => (
                <div key={index} className="group">
                  <div className="aspect-[3/4] overflow-hidden rounded-lg shadow-md mb-3">
                    <img
                      src={brochure.image}
                      alt={brochure.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="text-sm md:text-base font-medium text-foreground text-center">{brochure.title}</h3>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

    </div>
  );
};

export default Brochures;
